import { useMemo } from "react";
import { products } from "@/mocks/products";
import { categories } from "@/mocks/categories";
import { buildImageInventory } from "@/lib/panelData";
import { listCustomers, listOrders } from "@/lib/records";
import { panelModes } from "@/mocks/panelContent";
import { PanelCard, ReadOnlyNotice, StatCard } from "../components/PanelUI";
import type { PanelSectionId } from "@/pages/painel/types";

interface OverviewSectionProps {
  onGoTo: (id: PanelSectionId) => void;
}

export default function OverviewSection({ onGoTo }: OverviewSectionProps) {
  const inventory = useMemo(() => buildImageInventory(), []);
  const colorCount = useMemo(
    () => products.reduce((sum, product) => sum + product.colors.length, 0),
    [],
  );
  const localCustomers = listCustomers().length;
  const localOrders = listOrders().length;

  const shortcuts: { id: PanelSectionId; label: string; icon: string }[] = [
    { id: "ficha", label: "Ficha do site", icon: "ri-file-list-3-line" },
    { id: "editar-site", label: "Editar site", icon: "ri-edit-2-line" },
    { id: "catalogo", label: "Catálogo", icon: "ri-t-shirt-line" },
    { id: "pedidos", label: "Pedidos", icon: "ri-shopping-bag-3-line" },
  ];

  return (
    <div className="flex flex-col gap-6">
      <ReadOnlyNotice text={panelModes.viewNotice} />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Produtos" value={String(products.length)} hint="5 por categoria" icon="ri-t-shirt-line" />
        <StatCard label="Categorias" value={String(categories.length)} hint="7 + Novidades" icon="ri-apps-2-line" />
        <StatCard label="Variações de cor" value={String(colorCount)} hint="2 por produto" icon="ri-palette-line" />
        <StatCard label="Imagens" value={String(inventory.length)} hint="Inventariadas no painel" icon="ri-image-2-line" />
      </div>

      <PanelCard title="Resumo da operação" description="Dados calculados a partir do catálogo e dos registros locais deste navegador.">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard label="Clientes (local)" value={String(localCustomers)} icon="ri-user-heart-line" />
          <StatCard label="Pedidos (local)" value={String(localOrders)} icon="ri-receipt-line" />
          <StatCard label="Modalidades de entrega" value="7" icon="ri-truck-line" />
          <StatCard label="Indexação" value="index, follow" icon="ri-global-line" />
        </div>
      </PanelCard>

      <PanelCard title="Atalhos" description="Acesso rápido às áreas mais usadas.">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {shortcuts.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => onGoTo(item.id)}
              className="flex cursor-pointer items-center gap-3 rounded-lg border border-background-200 bg-background-100 px-4 py-3 text-left text-sm text-foreground-800 transition-colors hover:border-primary-300 hover:bg-primary-50"
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                <i className={`${item.icon} text-base`} />
              </span>
              {item.label}
            </button>
          ))}
        </div>
      </PanelCard>

      <PanelCard title="Sobre este painel">
        <ul className="flex flex-col gap-2 text-sm text-foreground-700">
          <li className="flex items-start gap-2">
            <i className="ri-check-line mt-0.5 text-base text-primary-600" />
            A estrutura atual do site permanece preservada — nada foi recriado.
          </li>
          <li className="flex items-start gap-2">
            <i className="ri-check-line mt-0.5 text-base text-primary-600" />
            Visitantes podem conhecer todos os campos, mas não alterar nada.
          </li>
          <li className="flex items-start gap-2">
            <i className="ri-check-line mt-0.5 text-base text-primary-600" />
            As edições ficam reservadas à administração Vellora, com autenticação segura e perfil
            vellora_admin.
          </li>
        </ul>
      </PanelCard>
    </div>
  );
}